/**
 * English pluralisation, vendored from the `pluralize` package (v8.0.0, MIT licence,
 * copyright (c) 2013 Blake Embrey) — https://github.com/plurals/pluralize
 *
 * Only the pluralisation half is kept. Singularisation, the `isPlural` / `isSingular` predicates,
 * the runtime rule-registration API and the UMD wrapper are all dropped, along with the pronoun and
 * verb irregulars (`I` → `we`, `is` → `are`, …) and the ~90 uncountable animals and demonyms that
 * have no bearing on UI copy.
 *
 * `PLURAL_RULES` is copied verbatim and is order-dependent — see the loop in `pluraliseWord`, which
 * walks it backwards so the general catch-all sits at the front. Do not reorder or remove entries:
 * the patterns overlap, so dropping one silently changes which rule wins for unrelated words.
 */

type PluralRule = readonly [pattern: RegExp, replacement: string];

/** Singular nouns whose plural can't be derived from a rule. */
const IRREGULAR_PLURALS: Readonly<Record<string, string>> = {
    anathema: "anathemata",
    axe: "axes",
    carve: "carves",
    die: "dice",
    dingo: "dingoes",
    dogma: "dogmata",
    eave: "eaves",
    echo: "echoes",
    foot: "feet",
    genus: "genera",
    goose: "geese",
    groove: "grooves",
    human: "humans",
    lemma: "lemmata",
    looey: "looies",
    ox: "oxen",
    passerby: "passersby",
    pickaxe: "pickaxes",
    proof: "proofs",
    quiz: "quizzes",
    schema: "schemata",
    stigma: "stigmata",
    stoma: "stomata",
    thief: "thieves",
    tooth: "teeth",
    tornado: "tornadoes",
    torpedo: "torpedoes",
    valve: "valves",
    viscus: "viscera",
    volcano: "volcanoes",
    yes: "yeses",
};

/** Plurals of {@link IRREGULAR_PLURALS}, which must be left untouched if they're passed in directly. */
const ALREADY_PLURAL: ReadonlySet<string> = new Set(Object.values(IRREGULAR_PLURALS));

/**
 * Nouns that are identical in both forms. Trimmed to the words that plausibly describe something in
 * the product — `feedback` is a local addition, as upstream pluralises it to "feedbacks".
 */
const UNCOUNTABLE: ReadonlySet<string> = new Set([
    "advice",
    "data",
    "equipment",
    "feedback",
    "information",
    "media",
    "metadata",
    "news",
    "series",
    "software",
    "species",
    "staff",
    "traffic",
]);

/** Copied verbatim from upstream. Order matters — read the note at the top of the file. */
const PLURAL_RULES: readonly PluralRule[] = [
    [/s?$/i, "s"],
    [/[^\u0000-\u007F]$/i, "$0"],
    [/([^aeiou]ese)$/i, "$1"],
    [/(ax|test)is$/i, "$1es"],
    [/(alias|[^aou]us|t[lm]as|gas|ris)$/i, "$1es"],
    [/(e[mn]u)s?$/i, "$1s"],
    [/([^l]ias|[aeiou]las|[ejzr]as|[iu]am)$/i, "$1"],
    [/(alumn|syllab|vir|radi|nucle|fung|cact|stimul|termin|bacill|foc|uter|loc|strat)(?:us|i)$/i, "$1i"],
    [/(alumn|alg|vertebr)(?:a|ae)$/i, "$1ae"],
    [/(seraph|cherub)(?:im)?$/i, "$1im"],
    [/(her|at|gr)o$/i, "$1oes"],
    [
        /(agend|addend|millenni|dat|extrem|bacteri|desiderat|strat|candelabr|errat|ov|symposi|curricul|automat|quor)(?:a|um)$/i,
        "$1a",
    ],
    [/(apheli|hyperbat|periheli|asyndet|noumen|phenomen|criteri|organ|prolegomen|hedr|automat)(?:a|on)$/i, "$1a"],
    [/sis$/i, "ses"],
    [/(?:(kni|wi|li)fe|(ar|l|ea|eo|oa|hoo)f)$/i, "$1$2ves"],
    [/([^aeiouy]|qu)y$/i, "$1ies"],
    [/([^ch][ieo][ln])ey$/i, "$1ies"],
    [/(x|ch|ss|sh|zz)$/i, "$1es"],
    [/(matr|cod|mur|sil|vert|ind|append)(?:ix|ex)$/i, "$1ices"],
    [/\b((?:tit)?m|l)(?:ice|ouse)$/i, "$1ice"],
    [/(pe)(?:rson|ople)$/i, "$1ople"],
    [/(child)(?:ren)?$/i, "$1ren"],
    [/eaux$/i, "$0"],
    [/m[ae]n$/i, "men"],
];

/**
 * Re-applies the casing of the original word to its replacement, so "Skill" pluralises to "Skills"
 * rather than "skills".
 * @param word - The word as it was passed in.
 * @param token - The replacement, which is always lower case.
 * @returns The replacement, cased to match the original word.
 */
const restoreCase = (word: string, token: string): string => {
    if (word === token) {
        return token;
    }

    if (word === word.toLowerCase()) {
        return token.toLowerCase();
    }

    if (word === word.toUpperCase()) {
        return token.toUpperCase();
    }

    if (word[0] === word[0]?.toUpperCase()) {
        return token.charAt(0).toUpperCase() + token.substring(1).toLowerCase();
    }

    return token.toLowerCase();
};

/**
 * Expands the `$1`, `$2`, … back-references in a rule's replacement.
 * @param replacement - The replacement containing back-references.
 * @param matches - The arguments handed to `String.prototype.replace`.
 * @returns The replacement with its back-references filled in.
 */
const interpolate = (replacement: string, matches: unknown[]): string => {
    return replacement.replace(/\$(\d{1,2})/g, (_, group: string) => {
        const match = matches[Number(group)];
        return typeof match === "string" ? match : "";
    });
};

/**
 * Applies a single rule to a word.
 * @param word - The word to transform.
 * @param rule - The matching rule.
 * @returns The transformed word.
 */
const applyRule = (word: string, [pattern, replacement]: PluralRule): string => {
    return word.replace(pattern, (...matches: unknown[]) => {
        const match = String(matches[0]);
        const result = interpolate(replacement, matches);

        // An empty match means the rule appended to the end of the word, so there's no matched text
        // to take the casing from — use the last character instead.
        if (match === "") {
            const index = Number(matches[1]);
            return restoreCase(word.charAt(index - 1), result);
        }

        return restoreCase(match, result);
    });
};

/**
 * Converts a single word to its plural form.
 * @param word - The word to pluralise.
 * @returns The plural form of the word.
 */
const pluraliseWord = (word: string): string => {
    const token = word.toLowerCase();

    if (ALREADY_PLURAL.has(token)) {
        return restoreCase(word, token);
    }

    const irregular = IRREGULAR_PLURALS[token];

    if (typeof irregular === "string") {
        return restoreCase(word, irregular);
    }

    if (token.length === 0 || UNCOUNTABLE.has(token)) {
        return word;
    }

    // Walk backwards so the most specific rule wins over the catch-all at the front.
    for (let index = PLURAL_RULES.length - 1; index >= 0; index--) {
        const rule = PLURAL_RULES[index];

        if (typeof rule !== "undefined" && rule[0].test(word)) {
            return applyRule(word, rule);
        }
    }

    return word;
};

/**
 * Pluralises a word based on a count, handling irregular plurals ("category" → "categories",
 * "status" → "statuses") and leaving uncountable nouns and already-plural words alone.
 *
 * Only the last word of a phrase is inflected, so tag group names like "resolution status" become
 * "resolution statuses".
 *
 * **Note:** this deliberately differs from upstream, which *singularises* when the count is one.
 * Descriptions here are client-configured names (tag groups are often already plural, e.g.
 * "Escalations"), and rewriting someone's configured name to "1 Escalation" is more surprising than
 * leaving it as they typed it. A count of one therefore returns the word untouched.
 * @param word - The word or phrase to pluralise.
 * @param count - How many of the thing there are. A count of one returns the word unchanged.
 * @returns The word in the form matching the count.
 */
export const pluralize = (word: string, count: number): string => {
    return count === 1 ? word : pluraliseWord(word);
};
